import React from 'react';
import {
  Check,
  Loader2,
  Lock,
  Paperclip,
  RotateCcw,
  Send,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../ui/utils';
import type {
  PerformanceNoteAttachment,
  PerformanceRatingLevel,
  PerformanceReview,
  PerformanceSchedule,
} from '../../types/performance.types';
import { performanceApi } from '../../utils/performance-api';
import {
  formatGap,
  formatPerformanceDate,
  formatScore,
  gapTone,

  reviewStatusTone,
} from '../../utils/performance';
import { PerformancePill } from './PerformancePrimitives';

type Side = 'self' | 'manager';

interface ReviewPanelProps {
  review: PerformanceReview | null;
  period: 'mid' | 'eoy';
  schedule: PerformanceSchedule;
  levels: PerformanceRatingLevel[];
  mode: 'self' | 'manager';
  personName: string;
  userId: number;
  onChanged: () => Promise<void> | void;
}

const fileInputId = 'performance-review-file';

const RatingControl: React.FC<{
  value: number | null;
  levels: PerformanceRatingLevel[];
  disabled?: boolean;
  onChange: (value: number) => void;
}> = ({ value, levels, disabled, onChange }) => {
  const ordered = [...levels].sort((a, b) => a.score - b.score);
  return (
    <div className="grid grid-cols-5 gap-1">
      {ordered.map((level) => {
        const selected = value === level.score;
        return (
          <button
            key={level.score}
            type="button"
            disabled={disabled}
            onClick={() => onChange(level.score)}
            title={`${level.label} — ${level.definition || ''}`}
            className={cn(
              'rounded-md border px-1 py-1.5 text-center transition-colors',
              selected
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-card text-foreground hover:bg-accent',
              disabled && 'cursor-default opacity-60'
            )}
          >
            <span className="block text-sm font-semibold tabular-nums">{level.score}</span>
            <span
              className={cn(
                'mt-0.5 block truncate text-[9px] leading-tight',
                selected ? 'text-primary-foreground/80' : 'text-muted-foreground'
              )}
            >
              {level.label.split(' ')[0]}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const StepBar: React.FC<{ review: PerformanceReview; schedule: PerformanceSchedule }> = ({
  review,
  schedule,
}) => {
  const released = schedule.status === 'Closed';
  const managerSubmitted = Boolean(review.managerSubmittedAt);
  const selfSubmitted = Boolean(review.selfSubmittedAt);
  const step = released ? 4 : managerSubmitted ? 2 : selfSubmitted ? 1 : 0;
  const steps = ['Self-review', 'Manager review', 'Calibration', 'Released'];

  return (
    <div className="flex items-center gap-0 overflow-x-auto">
      {steps.map((label, index) => {
        const done = index < step;
        const current = index === step;
        return (
          <React.Fragment key={label}>
            <div className="flex min-w-fit items-center gap-2">
              <span
                className={cn(
                  'grid h-6 w-6 shrink-0 place-items-center rounded-full border text-[11px] font-semibold',
                  done && 'border-success bg-success text-white',
                  current && 'border-primary bg-primary text-primary-foreground',
                  !done && !current && 'border-border bg-card text-muted-foreground'
                )}
              >
                {done ? <Check className="h-3 w-3" /> : index + 1}
              </span>
              <span
                className={cn(
                  'text-xs',
                  index <= step ? 'font-medium text-foreground' : 'text-muted-foreground'
                )}
              >
                {label}
              </span>
            </div>
            {index < steps.length - 1 ? (
              <span
                className={cn(
                  'mx-2 h-px min-w-4 flex-1',
                  index < step ? 'bg-success' : 'bg-border'
                )}
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const ReviewPanel: React.FC<ReviewPanelProps> = ({
  review,
  period,
  schedule,
  levels,
  mode,
  personName,
  userId,
  onChanged,
}) => {
  const [ratings, setRatings] = React.useState<Record<number, number>>({});
  const [comments, setComments] = React.useState<Record<number, string>>({});
  const [overall, setOverall] = React.useState('');
  const [attachments, setAttachments] = React.useState<Record<number, PerformanceNoteAttachment[]>>({});
  const [saving, setSaving] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [reopenOpen, setReopenOpen] = React.useState(false);
  const [uploadingFor, setUploadingFor] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!review) {
      setRatings({});
      setComments({});
      setOverall('');
      setAttachments({});
      return;
    }
    const nextRatings: Record<number, number> = {};
    const nextComments: Record<number, string> = {};
    const nextAttachments: Record<number, PerformanceNoteAttachment[]> = {};
    review.ratings.forEach((row) => {
      const rating = mode === 'self' ? row.selfRating : row.managerRating;
      if (rating) nextRatings[row.goalId] = rating;
      nextComments[row.goalId] =
        mode === 'self' ? row.selfComment : row.managerComment;
      nextAttachments[row.goalId] =
        mode === 'self' ? row.selfAttachments : row.managerAttachments;
    });
    setRatings(nextRatings);
    setComments(nextComments);
    setAttachments(nextAttachments);
    setOverall(
      mode === 'self' ? review.selfOverallComment : review.managerOverallComment
    );
  }, [review, mode]);

  if (!review) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
        <p className="text-sm font-semibold">This review has not been started</p>
        <p className="mt-1 text-sm text-muted-foreground">
          The {period === 'mid' ? 'mid-year' : 'end-of-year'} review opens on its scheduled
          date.
        </p>
      </div>
    );
  }

  const editable = mode === 'self' ? review.canEditSelf : review.canEditManager;
  const rows = review.ratings.filter((row) => row.weight > 0);
  const ratedCount = rows.filter((row) => ratings[row.goalId]).length;
  const canSubmit = mode === 'self' ? review.canSubmitSelf : review.canSubmitManager;

  const save = async (submit: boolean) => {
    setSaving(true);
    try {
      const payload = {
        ratings: rows.map((row) => ({
          goalId: row.goalId,
          rating: ratings[row.goalId] ?? null,
          comment: comments[row.goalId] || '',
          attachments: attachments[row.goalId] || [],
        })),
        overallComment: overall,
        submit,
      };
      const periodValue = period === 'mid' ? 'MidYear' : 'EndYear';
      if (mode === 'self') {
        await performanceApi.reviews.saveSelf(userId, periodValue, payload);
      } else {
        await performanceApi.reviews.saveManager(userId, periodValue, payload);
      }
      await onChanged();
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const upload = async (goalId: number, files: FileList | null) => {
    if (!files?.length) return;
    setUploadingFor(goalId);
    try {
      const uploaded: PerformanceNoteAttachment[] = [];
      for (const file of Array.from(files)) {
        const result = await performanceApi.upload(file);
        uploaded.push({
          url: result.url,
          key: result.key,
          filename: result.filename,
          size: result.size,
          mimetype: result.mimetype,
        });
      }
      setAttachments((current) => ({
        ...current,
        [goalId]: [...(current[goalId] || []), ...uploaded],
      }));
    } finally {
      setUploadingFor(null);
    }
  };

  const selfVisible = mode === 'self' || review.selfVisible;
  const managerVisible = mode === 'manager' || review.managerVisible;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <StepBar review={review} schedule={schedule} />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <PerformancePill tone={reviewStatusTone(review.status)}>{review.status}</PerformancePill>
            <span className="text-xs text-muted-foreground">
              Self-review {review.window.selfStart ? `${formatPerformanceDate(review.window.selfStart)} – ${formatPerformanceDate(review.window.selfEnd)}` : 'off'}
              {' · '}Manager {review.window.managerStart ? `${formatPerformanceDate(review.window.managerStart)} – ${formatPerformanceDate(review.window.managerEnd)}` : 'off'}
            </span>
          </div>
          {mode === 'self' && review.canReopenSelf ? (
            <Button variant="outline" size="sm" onClick={() => setReopenOpen(true)}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Re-open self-review
            </Button>
          ) : null}
        </div>
      </div>

      {mode === 'manager' && !review.selfSubmittedAt && !review.proceedWithoutSelf ? (
        <div className="rounded-xl border border-warning-border bg-warning-bg p-4">
          <div className="flex items-start gap-2">
            <Lock className="mt-0.5 h-4 w-4 text-warning" />
            <div>
              <p className="text-sm font-semibold text-warning">
                Waiting on {personName.split(' ')[0]}'s self-review
              </p>
              <p className="mt-0.5 text-sm text-warning/90">
                You can start the manager review once the self-review is submitted. If the window
                closes first, HR can let you proceed.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {review.proceedWithoutSelf && !review.selfSubmittedAt ? (
        <div className="rounded-[10px] border border-link/30 bg-selection px-3 py-2 text-sm text-link">
          HR allowed you to proceed without a self-review — “{review.proceedReason}”.
        </div>
      ) : null}

      <div className="space-y-3">
        {rows.map((row, index) => (
          <div key={row.goalId} className="rounded-xl border border-border bg-card p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">
                  Goal {index + 1} · {row.weight}% weight · {row.progress}% progress
                </p>
                <p className="mt-0.5 text-sm font-semibold">{row.title}</p>
              </div>
              {review.status === 'Released' || review.calibrated ? (
                <div className="flex items-center gap-1.5">
                  {row.calibratedRating && row.calibratedRating !== row.managerRating ? (
                    <PerformancePill tone="warning">
                      Calibrated {row.managerRating} → {row.calibratedRating}
                    </PerformancePill>
                  ) : null}
                  <PerformancePill tone={row.managerRating ? 'muted' : 'muted'}>
                    Final {row.calibratedRating ?? row.managerRating ?? '–'}
                  </PerformancePill>
                </div>
              ) : null}
            </div>

            <div className={cn('mt-3 grid gap-4', managerVisible && selfVisible && 'md:grid-cols-2')}>
              {selfVisible ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Self-review
                    </p>
                    {row.selfRating ? (
                      <PerformancePill tone="muted">{row.selfRating} / 5</PerformancePill>
                    ) : null}
                  </div>
                  {mode === 'self' ? (
                    <>
                      <RatingControl
                        value={ratings[row.goalId] ?? null}
                        levels={levels}
                        disabled={!editable}
                        onChange={(value) =>
                          setRatings((current) => ({ ...current, [row.goalId]: value }))
                        }
                      />
                      <Textarea
                        value={comments[row.goalId] || ''}
                        onChange={(event) =>
                          setComments((current) => ({
                            ...current,
                            [row.goalId]: event.target.value,
                          }))
                        }
                        placeholder="Comment on your result"
                        rows={2}
                        disabled={!editable}
                      />
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        {row.selfRating ? `${row.selfRating} / 5` : 'Not rated'}
                      </p>
                      {row.selfComment ? (
                        <p className="text-sm text-muted-foreground">{row.selfComment}</p>
                      ) : null}
                      {row.selfAttachments.length ? (
                        <div className="flex flex-wrap gap-2">
                          {row.selfAttachments.map((file) => (
                            <a
                              key={file.url}
                              href={file.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-link hover:underline"
                            >
                              <Paperclip className="h-3 w-3" /> {file.filename}
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </>
                  )}
                  {mode === 'self' && editable ? (
                    <div>
                      <input
                        id={`${fileInputId}-${row.goalId}`}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(event) => upload(row.goalId, event.target.files)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingFor === row.goalId}
                        onClick={() =>
                          document.getElementById(`${fileInputId}-${row.goalId}`)?.click()
                        }
                      >
                        {uploadingFor === row.goalId ? (
                          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Paperclip className="mr-1 h-3.5 w-3.5" />
                        )}
                        Attach evidence
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {managerVisible ? (
                <div className="space-y-2 md:border-l md:border-border md:pl-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Manager review
                    </p>
                    {row.managerRating ? (
                      <PerformancePill tone="muted">{row.managerRating} / 5</PerformancePill>
                    ) : null}
                  </div>
                  {mode === 'manager' ? (
                    <>
                      <RatingControl
                        value={ratings[row.goalId] ?? null}
                        levels={levels}
                        disabled={!editable}
                        onChange={(value) =>
                          setRatings((current) => ({ ...current, [row.goalId]: value }))
                        }
                      />
                      <Textarea
                        value={comments[row.goalId] || ''}
                        onChange={(event) =>
                          setComments((current) => ({
                            ...current,
                            [row.goalId]: event.target.value,
                          }))
                        }
                        placeholder="Comment on the rating"
                        rows={2}
                        disabled={!editable}
                      />
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        {row.managerRating ? `${row.managerRating} / 5` : 'Not visible'}
                      </p>
                      {row.managerComment ? (
                        <p className="text-sm text-muted-foreground">{row.managerComment}</p>
                      ) : null}
                    </>
                  )}
                  {row.gap != null ? (
                    <PerformancePill tone={gapTone(row.gap)}>
                      Gap {formatGap(row.gap)}
                    </PerformancePill>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      <div className={cn('grid gap-4', mode === 'manager' && 'md:grid-cols-2')}>
        <div className="rounded-xl border border-border bg-card p-4">
          <Label htmlFor="review-overall">
            {mode === 'self' ? 'Overall self-review comment' : 'Overall manager comment'}
          </Label>
          <Textarea
            id="review-overall"
            value={overall}
            onChange={(event) => setOverall(event.target.value)}
            rows={4}
            className="mt-2"
            disabled={!editable}
            placeholder="Summarise the year — what went well, what to change."
          />
          {editable ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {ratedCount} of {rows.length} goals rated
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => save(false)} disabled={saving}>
                  Save draft
                </Button>
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={!canSubmit || saving || ratedCount !== rows.length}
                >
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                  Submit review
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              {review.selfSubmittedAt && mode === 'self'
                ? `Submitted ${formatPerformanceDate(review.selfSubmittedAt)}`
                : review.managerSubmittedAt
                  ? `Submitted ${formatPerformanceDate(review.managerSubmittedAt)}`
                  : 'Read-only.'}
            </p>
          )}
        </div>

        <div className="space-y-3 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {mode === 'self' ? 'Your score' : 'Manager score'}
            </span>
            <span className="text-2xl font-semibold tabular-nums">
              {formatScore(mode === 'self' ? review.selfScore : review.managerScore)}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {review.band ? (
              <PerformancePill tone={review.band.tone}>{review.band.label}</PerformancePill>
            ) : null}
            {review.finalScore != null && review.finalScore !== review.managerScore ? (
              <PerformancePill tone="warning">
                Final {formatScore(review.finalScore)}
              </PerformancePill>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            {mode === 'self'
              ? review.status === 'Released'
                ? 'Results have been released. Your manager’s ratings and comments are shown above.'
                : review.managerSubmittedAt
                  ? 'Your manager has submitted their review. Results are released by HR when the cycle closes.'
                  : 'Your manager reviews after you submit. Results are released by HR when the cycle closes.'
              : review.status === 'Released'
                ? 'Results have been released to the employee.'
                : 'The self-review is visible after the employee submits. Calibration can adjust ratings after submission.'}
          </p>
          {mode === 'manager' && review.selfScore != null && review.managerScore != null ? (
            <div className="flex items-center justify-between border-t border-border pt-2 text-xs">
              <span className="text-muted-foreground">Gap</span>
              <PerformancePill tone={gapTone(review.selfScore - review.managerScore)}>
                {formatGap(review.selfScore - review.managerScore)}
                {review.selfScore - review.managerScore > 0 ? ' self-rated higher' : review.selfScore - review.managerScore < 0 ? ' self-rated lower' : ' aligned'}
              </PerformancePill>
            </div>
          ) : null}
          {mode === 'self' && review.managerOverallComment ? (
            <div className="border-t border-border pt-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Manager's overall comment
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{review.managerOverallComment}</p>
            </div>
          ) : null}
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Submit {mode === 'self' ? 'self-review' : 'manager review'}?</DialogTitle>
            <DialogDescription>
              Once submitted, {mode === 'self' ? 'you cannot change it unless your manager has not started their review.' : 'this review becomes read-only.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => save(true)} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Re-open self-review?</DialogTitle>
            <DialogDescription>
              This clears the submitted stamp so you can edit and submit again. Available only
              while your manager has not started their review.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                setSaving(true);
                try {
                  await performanceApi.reviews.reopenSelf(
                    userId,
                    period === 'mid' ? 'MidYear' : 'EndYear'
                  );
                  setReopenOpen(false);
                  await onChanged();
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
            >
              Re-open
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
